import { styled } from '@mui/material';

import { useMemo } from 'react';

import { ErrorWithNodeData } from '@app-velocity/common';
import { Alert, Typography } from '@app-velocity/ui';

import { NavigateIcon } from '@app-velocity/icons';
import { useI18n } from '@app-velocity/i18n';

import { selectNode } from '../../../core';

export type ErrorWithNavigateProps = {
  error: ErrorWithNodeData;
};

const StyledButton = styled('button')(({ theme }) => ({
  background: 'transparent',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: 'pointer',
  padding: 0,
  marginTop: theme.spacing(1),
  '&:disabled': {
    opacity: 0.5,
  },
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const Info = styled(Typography)({
  fontWeight: 400,
});

const formatPageName = (name?: string) => {
  if (!name) return '';
  if (name.length < 20) return name;

  return `${name.slice(20)}...`;
};

export const ErrorWithNavigate = ({ error }: ErrorWithNavigateProps) => {
  const { t } = useI18n();

  const navigateToElement = () => {
    if (!error.nodeId) return;
    selectNode(error.nodeId);
  };

  const message = useMemo(() => {
    switch (error.message) {
      case 'IMAGE_TOO_BIG':
        return t('prototypes:save_error.page_too_big', {
          page: formatPageName(error.nodeName),
        });
      case 'UNABLE_TO_GENERATE_IMAGE':
        return t('prototypes:save_error.unable_to_save_page_image');
      case 'NODE_NOT_FOUND':
        return t('prototypes:save_error.unable_to_generate_page');
      default:
        return error.message;
    }
  }, [error, t]);

  return (
    <Alert severity="error">
      {message}

      {error.message === 'IMAGE_TOO_BIG' ? (
        <Info>{t('prototypes:save_error.larger_sizes_coming_soon')}</Info>
      ) : null}
      <Wrapper>
        <StyledButton type="button" onClick={navigateToElement}>
          {t('prototypes:navigate')}
          <NavigateIcon />
        </StyledButton>
      </Wrapper>
    </Alert>
  );
};
